import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronRight, UserCircle2, Users, ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fmtKwp } from "../../lib/format";
import ModalShell from "./ModalShell";

/**
 * Rollup panel showing each Asset Manager's slice of the pipeline
 * (project count + total kWp). Admins can trigger a bulk reassign to
 * hand off a departing AM's whole portfolio in one action.
 */
export default function AssetManagerRollup({ byAssetManager, roleKeys, users, canManage, onReassignDone }) {
  const [expanded, setExpanded] = useState(true);
  const [reassigning, setReassigning] = useState(null);
  const items = byAssetManager || [];
  const hasAny = items.length > 0;
  return (
    <div className="mb-6 border border-[var(--line)] rounded-lg bg-white" data-testid="pm-am-rollup">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--surface-2,#F6F7F9)]"
        data-testid="pm-am-toggle"
      >
        <div className="flex items-center gap-2.5">
          <ChevronRight
            size={14} className="muted transition-transform"
            style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
          />
          <UserCircle2 size={14} style={{ color: "var(--accent-hover)" }} />
          <div className="font-semibold text-sm">By Asset Manager</div>
          <span className="text-[11px] muted">{items.length} bucket{items.length === 1 ? "" : "s"}</span>
        </div>
        <div className="text-[11px] muted">click to {expanded ? "collapse" : "expand"}</div>
      </button>
      {expanded && (
        <div className="border-t border-[var(--line)] overflow-x-auto">
          {!hasAny && <div className="px-4 py-6 text-center muted text-sm italic">No projects yet.</div>}
          {hasAny && (
            <table className="w-full text-sm" data-testid="pm-am-table">
              <thead>
                <tr className="text-left border-b border-[var(--line)] bg-[var(--surface-2,#F6F7F9)]">
                  <th className="px-4 py-2 label !text-[9px]">Asset Manager</th>
                  <th className="px-4 py-2 label !text-[9px]">Email</th>
                  <th className="px-4 py-2 label !text-[9px] text-right">Projects</th>
                  <th className="px-4 py-2 label !text-[9px] text-right">Capacity</th>
                  {canManage && <th className="px-4 py-2 label !text-[9px] text-right w-32">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--line-soft,#EEE)]">
                {items.map((am) => (
                  <tr key={am.asset_manager_id || "__u__"} data-testid={`pm-am-row-${am.asset_manager_id || "unassigned"}`}>
                    <td className="px-4 py-2 font-medium">
                      {am.asset_manager_name || "Unassigned"}
                      {!am.asset_manager_id && (
                        <span className="tag ml-2 text-[10px]" style={{ background: "#F7DCEC", color: "#8A5A2C" }}>unassigned</span>
                      )}
                    </td>
                    <td className="px-4 py-2 muted text-xs">{am.email || "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">{am.project_count}</td>
                    <td className="px-4 py-2 text-right font-mono text-base font-semibold" style={{ color: "var(--accent-hover)" }}>
                      {fmtKwp(am.total_kwp)}
                    </td>
                    {canManage && (
                      <td className="px-4 py-2 text-right">
                        {am.asset_manager_id && am.project_count > 0 && (
                          <button
                            className="btn-ghost text-xs"
                            onClick={() => setReassigning(am)}
                            data-testid={`pm-am-reassign-${am.asset_manager_id}`}
                            title="Bulk-transfer all this AM's projects to someone else"
                          >
                            <Users size={11} /> Reassign
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {reassigning && (
        <BulkReassignModal
          fromAM={reassigning}
          users={users}
          roleKeys={roleKeys}
          onClose={() => setReassigning(null)}
          onDone={() => { setReassigning(null); onReassignDone?.(); }}
        />
      )}
    </div>
  );
}


function BulkReassignModal({ fromAM, users, roleKeys, onClose, onDone }) {
  const [toUserId, setToUserId] = useState("");
  const [roleKey, setRoleKey] = useState("asset_manager");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/pm/bulk-reassign", {
        from_user_id: fromAM.asset_manager_id,
        to_user_id: toUserId || null,
        role_key: roleKey,
      });
      toast.success(
        toUserId
          ? `Reassigned ${data.updated} project${data.updated === 1 ? "" : "s"} to ${data.to_user?.name || "new owner"}.`
          : `Cleared ${roleKey.replace("_", " ")} on ${data.updated} project${data.updated === 1 ? "" : "s"}.`,
      );
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk reassign failed");
    } finally {
      setSaving(false);
    }
  };
  const targets = (users || []).filter((u) => u.id !== fromAM.asset_manager_id);
  return (
    <ModalShell onClose={onClose} testid="pm-bulk-reassign-modal">
      <div className="p-5 border-b border-[var(--line)]">
        <div className="label mb-1">Bulk reassign</div>
        <div className="font-display text-xl font-bold">Move projects from {fromAM.asset_manager_name}</div>
        <div className="muted text-xs mt-1">
          {fromAM.project_count} project{fromAM.project_count === 1 ? "" : "s"} · {fmtKwp(fromAM.total_kwp)}
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div>
          <label className="label mb-1.5 block">Reassign role</label>
          <select
            className="input-box w-full" value={roleKey}
            onChange={(e) => setRoleKey(e.target.value)}
            data-testid="pm-bulk-role"
          >
            {roleKeys.map((rk) => <option key={rk.key} value={rk.key}>{rk.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label mb-1.5 block">Transfer to</label>
          <select
            className="input-box w-full" value={toUserId}
            onChange={(e) => setToUserId(e.target.value)}
            data-testid="pm-bulk-target"
          >
            <option value="">— clear assignment —</option>
            {targets.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
          </select>
        </div>
        <div className="text-[11px] muted">
          One audit entry is written per project. Notifications are NOT sent — announce the change to the team separately.
        </div>
      </div>
      <div className="p-4 border-t border-[var(--line)] flex justify-end gap-2 bg-[var(--surface-2,#F6F7F9)]">
        <button className="btn-ghost" onClick={onClose} data-testid="pm-bulk-cancel">Cancel</button>
        <button className="btn-accent" onClick={submit} disabled={saving} data-testid="pm-bulk-submit">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
          {toUserId ? "Transfer" : "Clear"}
        </button>
      </div>
    </ModalShell>
  );
}
